import { FeatureFlagKey } from "@regrello/feature-flags-api";
import { FeatureFlagService } from "../../../services/FeatureFlagService";
import type { WorkflowIntakeContext } from "../../../ui2/workflowIntake/types/workflowIntake";
import { RoutePaths, RoutePathsWithId, RouteQueryStringKeys } from "./consts";

const WORKSPACE_REGEX = /\/workspace\/[\w-]+/;

/**
 * A regex matching a home page route.
 *
 * Assuming `RoutePaths.HOME` maps to "/", the trailing slash (/) may be present or missing. Both
 * case are valid home page routes:
 *
 * Valid:
 * - /workspace/123/
 * - /workspace/123/?param=123
 * - /workspace/123
 * - /workspace/123?param=123
 *
 * Invalid:
 * - /workspace/123/some-other-route
 */
const WORKSPACE_HOME_REGEX = new RegExp(`/workspace/[\\w-]+${RoutePaths.HOME}?$`);

function getWorkspacePrefix(): string {
  const match = WORKSPACE_REGEX.exec(window.location.pathname);
  return match ? match[0] : "";
}

export function getIsOnFormBuilderPage(): boolean {
  return window.location.pathname.includes(RoutePaths.SPECTRUM_FORM_BUILDER);
}

export function getIsOnHomePage(): boolean {
  return window.location.pathname === RoutePaths.HOME || WORKSPACE_HOME_REGEX.test(window.location.pathname);
}

export function createWorkflowRoute(workflowId: number): string {
  return `${getWorkspacePrefix()}${RoutePathsWithId.WORKFLOW}/${workflowId}`;
}

export function getRouteToTask(taskUuid: string) {
  // (clewis): Task navigation works by appending the task ID to the current URL as a query param.
  // Take care to preserve pother existing query parameters when we do this, otherwise we can cause
  // surprising unrelated view-state changes.
  //
  // Note: There are more idiomatic ways to do this in React components using React Router
  // useLocation/useNavigate or nuqs useQueryStates, but as of this writing `getRouteToTask` is a
  // pure util that is also used in several other pure utils, so we can't use hooks here.
  // Refactoring into a hook-based approach would be a large effort without clear benefit for now.
  const currentSearchParams = new URLSearchParams(window.location.search);
  currentSearchParams.set(RouteQueryStringKeys.TASK_ID, taskUuid);
  return `?${currentSearchParams.toString()}`;
}

export function getRouteToWorkflowTask(workflowId: number, taskUuid: string) {
  return `${createWorkflowRoute(workflowId)}${getRouteToTask(taskUuid)}`;
}

export function getRouteToWorkflowTaskComment(workflowId: number, taskUuid: string, commentId: number) {
  return `${createWorkflowRoute(workflowId)}${getRouteToTaskComment(taskUuid, commentId)}`;
}

export function getRouteToTaskComment(taskUuid: string, commentId: number) {
  return `?${RouteQueryStringKeys.TASK_ID}=${taskUuid}&${RouteQueryStringKeys.COMMENT_ID}=${commentId}`;
}

export function getRouteToFields() {
  return getWorkspaceRouteTo(RoutePaths.CUSTOM_FIELDS);
}

export function getRouteToFormsPublished() {
  return getWorkspaceRouteTo(RoutePaths.SPECTRUM_FORMS);
}

export function getRouteToWorkflow(workflowId: number) {
  return getRouteWithId(RoutePathsWithId.WORKFLOW, workflowId);
}

export function getRouteToWorkflowIntake(workflowTemplateId: number, workflowIntakeContext: WorkflowIntakeContext) {
  return `${getRouteWithId(RoutePathsWithId.WORKFLOW_INTAKE, workflowTemplateId)}?${RouteQueryStringKeys.WORKFLOW_INTAKE_CONTEXT}=${workflowIntakeContext}`;
}

export function getRouteToWorkflowTemplate(workflowTemplateId: number) {
  return getRouteWithId(RoutePathsWithId.BLUEPRINTS, workflowTemplateId);
}

export function getRouteToLatestWorkflowTemplate(blueprintUuId: string) {
  return getRouteWithUuid(RoutePathsWithId.LATEST_WORKFLOW_TEMPLATE, blueprintUuId);
}

export function getRouteToFormBuilder(queryParams: { formUuid: string; displayMode?: "page" | "panel" }): string {
  const path = getWorkspaceRouteTo(RoutePaths.SPECTRUM_FORM_BUILDER);
  const searchParams = new URLSearchParams();

  searchParams.set(RouteQueryStringKeys.UUID, queryParams.formUuid);

  if (queryParams.displayMode != null) {
    searchParams.set(RouteQueryStringKeys.DISPLAY_MODE, queryParams.displayMode);
  }

  return `${path}?${searchParams.toString()}`;
}

export function getWorkspaceRouteTo(routePath: string) {
  return `${getWorkspacePrefix()}${routePath}`;
}

function getRouteWithId(route: RoutePathsWithId, id: number) {
  return `${getWorkspacePrefix()}${route}/${id}`;
}

function getRouteWithUuid(route: RoutePathsWithId, id: string) {
  return `${getWorkspacePrefix()}${route}/${id}`;
}

export const maybeRemoveFirstSlash = (path: string) => {
  return path.startsWith("/") ? path.slice(1) : path;
};

export function getWorkflowTemplateLink(id: number, blueprintUuid: string) {
  const isBlueprintVersionLinksEnabled = FeatureFlagService.isEnabled(FeatureFlagKey.BLUEPRINT_VERSION_LINKS_2025_08);
  return `${window?.location?.origin}${
    isBlueprintVersionLinksEnabled ? getRouteToLatestWorkflowTemplate(blueprintUuid) : getRouteToWorkflowTemplate(id)
  }`;
}
